import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { AccountMarketplace, AlgoMode, CampaignType, StrategyStateEnum } from "@front/m19-api-client";
import { AccountMarketplaceEx, StrategyEx } from "@front/m19-models";
import { AccountSelectionService, AuthService, StrategyCache } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IBadgeComponent,
  IButtonDirective,
  IDateRange,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  IInputDirective,
  IPaginationConfig,
  IPaginatorComponent,
  ITableImports,
  IToggleComponent,
  ITooltipDirective,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { DateUtils } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AmazonTimezoneService } from "@m19-board/utils/amazonTimezoneService";
import { map, switchMap, tap } from "rxjs/operators";
import { BulkImportService } from "../strategy-bulk-upload-modal/bulk-import.service";

import { formatDate } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { StrategyService } from "@front/m19-injection-token";
import {
  PromoDaysBoostDateRangeModalComponent,
  PromoDaysBoostDateRangeModalData,
} from "@m19-board/strategies/promo-days-boost/promo-days-boost-date-range-modal/promo-days-boost-date-range-modal.component";
import { DateTime } from "luxon";
import { Observable, of } from "rxjs";
import { PromoDaysBoostListRowComponent } from "./promo-days-boost-list-row.component";

@Component({
  selector: "app-promo-days-boost",
  templateUrl: "./promo-days-boost.component.html",
  imports: [
    TranslocoDirective,
    IButtonDirective,
    ITooltipDirective,
    IToggleComponent,
    ITableImports,
    IBadgeComponent,
    PromoDaysBoostListRowComponent,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
    IButtonDirective,
    IInputDirective,
    FormsModule,
    IPaginatorComponent,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromoDaysBoostComponent {
  private accountSelectionService = inject(AccountSelectionService);
  private authService = inject(AuthService);
  private toasterService = inject(ToasterService);
  private modalService = inject(ModalService);
  private amazonTzService = inject(AmazonTimezoneService);
  private bulkImportService = inject(BulkImportService);
  private strategyService = inject(StrategyService);
  private strategyCache = inject(StrategyCache);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);

  readonly DEFAULT_PAGE_SIZE = 20;
  readonly minDate = DateTime.utc();
  readonly maxDate = DateTime.utc().plus({ year: 10 });
  readonly AlgoMode = AlgoMode;

  readonly user = toSignal(this.authService.loggedUser$, { requireSync: true });
  readonly accountMarketplace = toSignal<AccountMarketplace>(
    this.accountSelectionService.singleAccountMarketplaceSelection$,
    { requireSync: true },
  );

  readonly strategies = toSignal<StrategyEx[] | undefined>(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.strategyCache.getAllStrategyIndex(am.accountId, am.marketplace)),
      map((strategyIndex) =>
        Array.from(strategyIndex.values()).filter(
          (s) => s.state !== StrategyStateEnum.PAUSED && !s.isTacos() && s.campaignType !== CampaignType.DSP,
        ),
      ),
    ),
  );

  readonly isReadOnly = toSignal(this.accountSelectionService.readOnlyMode$, { requireSync: true });

  readonly isPromoDaysActivated = computed(() => {
    const am = this.accountMarketplace();
    return !!(am?.promoStartDate && am?.promoEndDate);
  });
  readonly dateRange = computed<IDateRange | null>(() => {
    const am = this.accountMarketplace();
    if (am?.promoStartDate && am?.promoEndDate) {
      return { start: DateTime.fromISO(am.promoStartDate), end: DateTime.fromISO(am.promoEndDate) };
    }
    const today = this.amazonTzService.getNow(am.marketplace);
    return { start: today, end: today.plus({ days: 5 }) };
  });
  readonly startDateFmt = computed(() =>
    this.dateRange()?.start
      ? formatDate(this.dateRange()!.start!.toJSDate()!, "mediumDate", this.user()?.locale ?? "en-US")
      : "-",
  );
  readonly endDateFmt = computed(() =>
    this.dateRange()?.end
      ? formatDate(this.dateRange()!.end!.toJSDate()!, "mediumDate", this.user()?.locale ?? "en-US")
      : "-",
  );
  readonly dateFormat = computed(() => DateUtils.getDateFormatString(this.user()?.locale ?? "en-US"));

  readonly isToggleDisabled = computed(
    () => this.isReadOnly() || !this.strategies() || this.strategies()!.length === 0,
  );

  readonly toogleTooltipValue = computed(() => {
    if (this.isToggleDisabled() && !this.strategies()?.length) {
      return this.translocoService.translate("strategy-boost.no_live_strategies_on_this_marketplace");
    }
    return undefined;
  });

  readonly filteredStrategies = computed(() =>
    this.strategies()?.filter((s) => s.name.toLowerCase().includes(this.filter().toLowerCase())),
  );

  paginatedStrategies = computed<StrategyEx[]>(() => {
    if (!this.filteredStrategies()) return [];
    if (this.filteredStrategies()!.length <= this.DEFAULT_PAGE_SIZE) return this.filteredStrategies()!;

    const start = this.pagination().pageIndex * this.pagination().pageSize;
    const end = start + this.pagination().pageSize;

    return this.filteredStrategies()?.slice(start, end) ?? [];
  });

  pagination = signal<IPaginationConfig>({
    pageIndex: 0,
    pageSize: this.DEFAULT_PAGE_SIZE,
    length: 0,
  });
  promoDaysLoading = signal<boolean>(false);
  filter = signal<string>("");
  hasPromoDaysStarted = computed(
    () =>
      this.dateRange() &&
      this.dateRange()!.start &&
      this.dateRange()!.start! < DateTime.now() &&
      this.dateRange()!.end &&
      this.dateRange()!.end! > DateTime.now(),
  );

  editPromoDaysDateRange() {
    this.openPromoDaysDateRangeModal(true).subscribe({
      next: () => {
        this.toasterService.success(this.translocoService.translate("strategy-boost.promo_days_period_updated"));
      },
      error: (error) => {
        this.toasterService.error(error.message);
      },
    });
  }

  togglePromoDaysActivation() {
    const isActivating = !this.isPromoDaysActivated();
    const togglePromoDaysBoost$ = isActivating ? this.openPromoDaysDateRangeModal(false) : this.deactivatePromoDays();

    togglePromoDaysBoost$.subscribe({
      next: () => {
        this.toasterService.success(
          this.translocoService.translate(
            `strategy-boost.${isActivating ? "promo_days_activated" : "promo_days_deactivated"}`,
          ),
        );
        this.promoDaysLoading.set(false);
      },
      error: (error) => {
        this.promoDaysLoading.set(false);
        this.toasterService.error(error.message);
      },
    });
  }

  private deactivatePromoDays(): Observable<AccountMarketplaceEx[]> {
    let askConfirmation$: Observable<unknown> = of(undefined);
    if (this.hasPromoDaysStarted()) {
      askConfirmation$ = this.modalService
        .openModal<ConfirmPopupData, void>(ConfirmPopupComponent, {
          modalTitle: "Deactivate Promo Days",
          data: {
            message:
              "Promo days are currently running. ACOS Target of strategies with boost will go back to their initial values.",
            confirmLabel: "Deactivate Promo Days",
          },
        })
        .afterClosed();
    }

    return askConfirmation$.pipe(
      tap(() => this.promoDaysLoading.set(true)),
      switchMap(() => this.updatePromoDays(this.dateRange()!, true)),
    );
  }

  private openPromoDaysDateRangeModal(isEdit: boolean) {
    const modalRef = this.modalService.openModal<PromoDaysBoostDateRangeModalData, IDateRange>(
      PromoDaysBoostDateRangeModalComponent,
      {
        size: "md",
        modalTitle: isEdit ? "Edit Promo Days Date Range" : "Activate Promo Days",
        data: {
          dateRange: this.dateRange()!,
          minDate: this.minDate,
          maxDate: this.maxDate,
          locale: this.user()?.locale ?? "en-US",
          isEdit: isEdit,
        },
      },
    );

    return modalRef.afterClosed().pipe(
      tap(() => {
        if (!isEdit) {
          this.promoDaysLoading.set(true);
        }
      }),
      switchMap((dateRange) => this.updatePromoDays(dateRange!, false)),
    );
  }

  uploadPromoDays() {
    void this.router.navigate(["/bulk/promo-boost"]);
  }

  exportAsCsv() {
    this.bulkImportService.exportPromoDaysBoostCsv();
  }

  private updatePromoDays(dateRange: IDateRange, deactivate: boolean) {
    return this.strategyService.togglePromoDays(
      this.accountMarketplace()!,
      DateUtils.formatLuxonDate(dateRange.start!),
      DateUtils.formatLuxonDate(dateRange.end!),
      deactivate,
    );
  }
}
